import React, { useEffect, useState } from 'react';
import { formatMoney, formatNumber, formatDate, formatDateTime } from '../../utils/formatters';
import { getHrEmployee, updateHrEmployee, deleteHrEmployee } from '../../services/api';
import { rateLabel, unitOf } from './tsFormat';

const FIELD_LABELS = {
  hours: 'Часы', rate: 'Ставка', advance1: 'За 1–15', advance2: 'За 16–конец',
  bonus: 'Доплаты', fines: 'Штрафы', total: 'Итого', comment: 'Комментарий',
};

const EmployeeDrawer = ({ employeeId, isAdmin, onClose, onEdit, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getHrEmployee(employeeId)
      .then(result => { if (alive) setData(result); })
      .catch(() => { if (alive) setError('Не удалось загрузить карточку'); });
    return () => { alive = false; };
  }, [employeeId]);

  // Закрытие по Escape — у бокового окна нет другой очевидной клавиши выхода
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const employee = data?.employee;
  const history = data?.history || [];
  const log = data?.log || [];
  // Две должности в одном месяце — одна полоска на графике.
  const monthly = Object.values(history.reduce((acc, h) => {
    const key = `${h.year}-${h.month}`;
    acc[key] = acc[key] ? { ...acc[key], total: acc[key].total + h.total } : { ...h };
    return acc;
  }, {}));
  const maxTotal = Math.max(...monthly.map(h => h.total), 1);

  const toggleStatus = async () => {
    setBusy(true);
    try {
      const next = employee.status === 'active' ? 'fired' : 'active';
      await updateHrEmployee(employee.id, { status: next });
      setData(prev => ({ ...prev, employee: { ...prev.employee, status: next } }));
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Удалить ${employee.full_name} из справочника?`)) return;
    setBusy(true);
    try {
      const result = await deleteHrEmployee(employee.id);
      onChanged();
      if (result.fired) {
        // У человека есть строки табеля — удалять нельзя, иначе история
        // выплат осталась бы без владельца. Сервер перевёл его в «уволен».
        window.alert(`За сотрудником числится ${result.entries} строк табеля, поэтому он переведён в «уволенные», а не удалён.`);
        setData(prev => ({ ...prev, employee: { ...prev.employee, status: 'fired' } }));
        setBusy(false);
      } else {
        onClose();
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось удалить');
      setBusy(false);
    }
  };

  return (
    <div className="hr-drawer-overlay" onClick={onClose}>
      <aside className="hr-drawer" onClick={(e) => e.stopPropagation()}>
        {error && <div className="ui-inline-error">{error}</div>}
        {!employee ? (
          <div className="hr-empty"><div className="hr-empty-title">Загрузка…</div></div>
        ) : (
          <>
            <header className="hr-drawer-head">
              <div>
                <h2>{employee.full_name}</h2>
                <p className="hr-drawer-sub">
                  {employee.position}
                  {employee.status === 'fired' && <span className="hr-chip fired">уволен</span>}
                </p>
              </div>
              <button className="ui-icon-btn" onClick={onClose} aria-label="Закрыть">×</button>
            </header>

            <div className="hr-drawer-actions">
              <button className="ui-btn-primary" onClick={() => onEdit(employee)}>Редактировать</button>
              <button className="ui-btn-ghost" onClick={toggleStatus} disabled={busy}>
                {employee.status === 'active' ? 'Уволить' : 'Вернуть в штат'}
              </button>
              {isAdmin && (
                <button className="ui-btn-danger" onClick={remove} disabled={busy}>Удалить</button>
              )}
            </div>

            <section className="hr-drawer-section">
              <h3>Оплата и контакты</h3>
              <dl className="ui-defs">
                <div>
                  <dt>Тип оплаты</dt>
                  <dd>
                    {employee.pay_type === 'percent'
                      ? `${employee.percent}% от выручки`
                      : rateLabel(employee.pay_type, employee.rate)}
                  </dd>
                </div>
                <div><dt>Телефон</dt><dd>{employee.phone || '—'}</dd></div>
                <div><dt>Email</dt><dd>{employee.email || '—'}</dd></div>
                <div><dt>Дата приёма</dt><dd>{employee.hire_date ? formatDate(employee.hire_date) : '—'}</dd></div>
                <div><dt>Связь с iiko</dt><dd>{employee.iiko_employee_id ? 'есть' : 'нет'}</dd></div>
                <div><dt>Учётка в системе</dt><dd>{employee.user_id ? 'привязана' : 'не привязана'}</dd></div>
              </dl>
              {employee.notes && <p className="hr-drawer-notes">{employee.notes}</p>}
            </section>

            <section className="hr-drawer-section">
              <h3>История выплат</h3>
              {history.length === 0 ? (
                <p className="ui-dim">По этому сотруднику ещё нет ни одного месяца в табеле.</p>
              ) : (
                <>
                  <div className="hr-spark">
                    {[...monthly].sort((x, y) => (x.year - y.year) || (x.month - y.month)).map(month => (
                      <div className="hr-spark-col" key={`${month.year}-${month.month}`} title={`${month.label}: ${formatMoney(month.total)}`}>
                        <div className="hr-spark-bar" style={{ height: `${Math.max(4, (month.total / maxTotal) * 100)}%` }} />
                        <span className="hr-spark-label">{month.short_label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="hr-table-wrap">
                    <table className="hr-table compact">
                      <thead>
                        <tr>
                          <th>Месяц</th><th>Должность</th><th>Отработано</th><th>Ставка</th><th>За 1–15</th><th>За 16–конец</th><th>Итого</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map(month => (
                          <tr key={month.entry_id}>
                            <td>{month.label}</td>
                            <td>{month.position}</td>
                            <td>{formatNumber(month.hours)} {unitOf(month.pay_type, month.hours)}</td>
                            <td>{formatNumber(month.rate)} ₽</td>
                            <td>{formatMoney(month.advance1)}</td>
                            <td>{formatMoney(month.advance2)}</td>
                            <td className="hr-num-strong">{formatMoney(month.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>

            <section className="hr-drawer-section">
              <h3>История правок</h3>
              {log.length === 0 ? (
                <p className="ui-dim">Табель по этому сотруднику ещё не правили.</p>
              ) : (
                <ul className="hr-log">
                  {log.map(record => (
                    <li key={record.id}>
                      <span className="hr-log-field">{FIELD_LABELS[record.field] || record.field}</span>
                      <span className="hr-log-change">{record.old_value} → {record.new_value}</span>
                      <span className="hr-log-meta">{record.user_name || '—'}, {formatDateTime(record.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </aside>
    </div>
  );
};

export default EmployeeDrawer;
